import { createContext, type ReactNode, useContext } from 'react';

type PopoverBoundaryContextValue = {
  /** Radix の collisionBoundary に渡す DOM 要素。null は「親 boundary なし」 */
  boundary: Element | null;
  /** Radix の collisionPadding。未指定なら PopoverContent 側のフォールバック (8px) を採用 */
  collisionPadding?: number;
  /**
   * 内側 PopoverContent に適用する z-index。未指定なら PopoverContent 既定（z-50）。
   * 親 Popover（FilterPopover 等）が自身より上に内側ドロップダウンを重ねたい場合に渡す。
   */
  contentZIndex?: number;
  /**
   * 内側 Popover を modal にするか（Radix Popover の modal）。true で開いている間は背景スクロールを
   * ロックする（Radix Select と同じ挙動）。未指定なら Radix 既定（false）。
   */
  modal?: boolean;
};

const PopoverBoundaryContext = createContext<PopoverBoundaryContextValue>({
  boundary: null,
  collisionPadding: undefined,
  contentZIndex: undefined,
  modal: undefined,
});

type Props = {
  children: ReactNode;
  /**
   * 衝突回避の基準となる DOM 要素。
   * `useState<HTMLDivElement | null>(null)` + callback ref で取得した値を渡す。
   * `useRef` を使うと再レンダリングが起きず Context が更新されない点に注意。
   */
  boundary: Element | null;
  /** 親 boundary との余白（px）。未指定なら 8 */
  collisionPadding?: number;
  /** 内側 PopoverContent に適用する z-index。未指定なら PopoverContent 既定（z-50）。 */
  contentZIndex?: number;
  /** 内側 Popover を modal にするか（背景スクロールロック）。未指定なら Radix 既定（false）。 */
  modal?: boolean;
};

/**
 * 子 Popover に対して衝突回避の境界要素を提供する Provider。
 *
 * FilterPopover のように内部に別の Popover（MultipleSelect / Cascader 等）を
 * 含むケースで、内側 Popover が親の枠を超えないようにするために使用する。
 *
 * 内側で `PopoverContent` を使うコンポーネントは自動的にこの Context を consume し、
 * Radix の `collisionBoundary` / `collisionPadding` に渡す。
 */
export const PopoverBoundaryProvider = ({
  children,
  boundary,
  collisionPadding,
  contentZIndex,
  modal,
}: Props) => (
  <PopoverBoundaryContext.Provider value={{ boundary, collisionPadding, contentZIndex, modal }}>
    {children}
  </PopoverBoundaryContext.Provider>
);

export const usePopoverBoundary = () => useContext(PopoverBoundaryContext);
